import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, TextField, Select, MenuItem } from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";

import useCaptcha from "../../../hooks/super/useCaptcha";
import useAuth from "../../../hooks/useAuth";

import HcaptchaSiteKeyHistoryList from "./component/T_HcaptchaSiteKeyHistory";
import HcaptchaSecretKeyHistoryList from "./component/T_HcaptchaSecretKeyHistory";
import RecaptchaApiKeyHistoryList from "./component/T_RecaptchaApiKeyHistory";
import { CaptchaType, UserType } from "../../../utils/constants";

import { CollapseAlert } from "../../../components/pages/common/styled";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function SACaptchaApiKeys() {
  const {
    getHcaptchaSiteKeyHistory,
    hCaptchaSiteKeySize,
    insertHcaptchaSiteKey,
    getHcaptchaSecretKeyHistory,
    hCaptchaSecretKeySize,
    insertHcaptchaSecretKey,
    getRecaptchaApiKeyHistory,
    reCaptchaApiKeySize,
    insertRecaptchaApiKey,
    errMsg,
    setErr,
  } = useCaptcha();
  const { isAuthenticated, adminUserType } = useAuth();

  const hCaptchaSiteKeyRef = React.useRef(null);
  const [hCaptchaSiteKeyValue, setHcaptchaSiteKeyValue] = React.useState("");
  const hCaptchaSecretKeyRef = React.useRef(null);
  const [hCaptchaSecretKeyValue, setHcaptchaSecretKeyValue] = React.useState("");
  const reCaptchaSiteKeyRef = React.useRef(null);
  const [reCaptchaSiteKeyValue, setRecaptchaSiteKeyValue] = React.useState("");
  const reCaptchaSecretKeyRef = React.useRef(null);
  const [reCaptchaSecretKeyValue, setRecaptchaSecretKeyValue] = React.useState("");
  const [reCaptchaType, setRecaptchaType] = React.useState(CaptchaType.RECAPTCHA_V3);
  const selectRecaptchaType = (e) => setRecaptchaType(e.target.value);

  const saveHcaptchaSiteKey = () => {
    if (!hCaptchaSiteKeyValue) {
      hCaptchaSiteKeyRef.current?.focus();
      return;
    }
    insertHcaptchaSiteKey(hCaptchaSiteKeyValue);
    setHcaptchaSiteKeyValue("");
  };
  const changeHcaptchaSiteKey = (e) => setHcaptchaSiteKeyValue(e.target.value);

  const saveHcaptchaSecretKey = () => {
    if (!hCaptchaSecretKeyValue) {
      hCaptchaSecretKeyRef.current?.focus();
      return;
    }
    insertHcaptchaSecretKey(hCaptchaSecretKeyValue);
    setHcaptchaSecretKeyValue("");
  };
  const changeHcaptchaSecretKey = (e) => setHcaptchaSecretKeyValue(e.target.value);

  const saveRecaptchaApiKey = () => {
    if (!reCaptchaSiteKeyValue) {
      reCaptchaSiteKeyRef.current?.focus();
      return;
    }
    if (!reCaptchaSecretKeyValue) {
      reCaptchaSecretKeyRef.current?.focus();
      return;
    }
    insertRecaptchaApiKey(reCaptchaType, reCaptchaSiteKeyValue, reCaptchaSecretKeyValue);
    setHcaptchaSiteKeyValue("");
  };
  const changeRecaptchaSiteKey = (e) => setRecaptchaSiteKeyValue(e.target.value);
  const changeRecaptchaSecretKey = (e) => setRecaptchaSecretKeyValue(e.target.value);

  React.useEffect(() => {
    if (isAuthenticated) {
      getHcaptchaSiteKeyHistory(hCaptchaSiteKeySize, 0);
      getHcaptchaSecretKeyHistory(hCaptchaSecretKeySize, 0);
      if (undefined !== reCaptchaType) {
        getRecaptchaApiKeyHistory(reCaptchaType, reCaptchaApiKeySize, 0);
      }
    }
    return () => setErr(null);
  }, [
    isAuthenticated,
    hCaptchaSiteKeySize,
    hCaptchaSecretKeySize,
    reCaptchaApiKeySize,
    reCaptchaType,
    setErr,
    getHcaptchaSiteKeyHistory,
    getHcaptchaSecretKeyHistory,
    getRecaptchaApiKeyHistory,
  ]);

  React.useEffect(() => {
    if (undefined !== reCaptchaType) {
      getRecaptchaApiKeyHistory(reCaptchaType, reCaptchaApiKeySize, 0);
      setRecaptchaSiteKeyValue("");
      setRecaptchaSecretKeyValue("");
    }
  }, [reCaptchaType, reCaptchaApiKeySize, getRecaptchaApiKeyHistory]);

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const labelSx = { fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 };
  const isSuper = UserType.SUPER_ADMIN === adminUserType;

  return (
    <React.Fragment>
      <Helmet title="SA Captcha API Keys" />
      <PageHeader title="Captcha API Keys" description="Manage hCaptcha and reCaptcha credentials used by the WAF" />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={2}>
        <SectionCard title="hCaptcha Site Key" description="Public key embedded in challenge pages">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ maxWidth: 720, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="hCaptcha site key"
              required
              value={hCaptchaSiteKeyValue}
              onChange={changeHcaptchaSiteKey}
              inputRef={hCaptchaSiteKeyRef}
              sx={inputSx}
            />
            <PrimaryButton
              onClick={saveHcaptchaSiteKey}
              startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
              disabled={!isSuper}
            >
              Save
            </PrimaryButton>
          </Stack>
          <HcaptchaSiteKeyHistoryList />
        </SectionCard>

        <SectionCard title="hCaptcha Secret Key" description="Server-side verification token">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ maxWidth: 720, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="hCaptcha secret key"
              required
              value={hCaptchaSecretKeyValue}
              onChange={changeHcaptchaSecretKey}
              inputRef={hCaptchaSecretKeyRef}
              sx={inputSx}
            />
            <PrimaryButton
              onClick={saveHcaptchaSecretKey}
              startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
              disabled={!isSuper}
            >
              Save
            </PrimaryButton>
          </Stack>
          <HcaptchaSecretKeyHistoryList />
        </SectionCard>

        <SectionCard title="reCaptcha API Keys" description="Choose a Google reCaptcha variant, then enter its credentials">
          <Stack spacing={2.5}>
            <Box sx={{ maxWidth: 360 }}>
              <Typography sx={labelSx}>reCaptcha type</Typography>
              <Select
                value={reCaptchaType !== undefined && reCaptchaType}
                onChange={selectRecaptchaType}
                fullWidth
                size="small"
                sx={{
                  borderRadius: `${TOKENS.radiusXs}px`,
                  "& .MuiOutlinedInput-input": { fontSize: 14 },
                }}
              >
                <MenuItem value={CaptchaType.RECAPTCHA_V2_CHECKBOX}>reCAPTCHAv2 Checkbox</MenuItem>
                <MenuItem value={CaptchaType.RECAPTCHA_V2_INVISIBLE}>reCAPTCHAv2 Invisible</MenuItem>
                <MenuItem value={CaptchaType.RECAPTCHA_V3}>reCAPTCHAv3</MenuItem>
              </Select>
            </Box>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>reCaptcha site key</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Site key"
                  required
                  value={reCaptchaSiteKeyValue}
                  onChange={changeRecaptchaSiteKey}
                  inputRef={reCaptchaSiteKeyRef}
                  sx={inputSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>reCaptcha secret key</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Secret key"
                  required
                  value={reCaptchaSecretKeyValue}
                  onChange={changeRecaptchaSecretKey}
                  inputRef={reCaptchaSecretKeyRef}
                  sx={inputSx}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                <PrimaryButton
                  onClick={saveRecaptchaApiKey}
                  startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
                  disabled={!isSuper}
                >
                  Save
                </PrimaryButton>
              </Box>
            </Stack>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <RecaptchaApiKeyHistoryList />
          </Box>
        </SectionCard>
      </Stack>
    </React.Fragment>
  );
}
export default SACaptchaApiKeys;
